import { Component, input, inject, computed, signal } from "@angular/core";
import { Router } from "@angular/router";
import {
    INewDwellingDocumentInfo,
    INewDwellingInfo,
    INewDwellingProjectInfo,
    INewDwellingPublication,
} from "src/app/infopage/new-dwelling/newdwelling.model";
import { NewDwellingDataService } from "src/app/infopage/services/new-dwelling-data.service";
import { EvLogoLoaderComponent } from "src/app/shared-components/ev-logo-loader/ev-logo-loader.component";

@Component({
    selector: "infopage-links-nd",
    imports: [EvLogoLoaderComponent],
    templateUrl: "./infopage-links.component.html",
    styleUrl: "./infopage-links.component.scss",
})
export class InfopageLinksNewDwellingComponent {
    dwellingStageInfo = input.required<INewDwellingInfo>();
    dwellingProjectInfo = input.required<INewDwellingProjectInfo>();
    publications = input<INewDwellingPublication[]>([]);
    stageMode = input<boolean>(false);
    prospectPdf = input<string>();
    miniInfocard = input<boolean>(false);
    prospectIsLoading = signal<boolean>(false);

    private readonly newDwellingService = inject(NewDwellingDataService);
    private readonly router = inject(Router);

    infopageUrl = computed(() => {
        const pid = this.dwellingProjectInfo()?.unitId;
        const pseid = this.stageMode() ? this.dwellingStageInfo()?.projectStageEstateTypeId : null;
        return this.router.serializeUrl(
            this.router.createUrlTree(["/infopage/newdwelling/project"], {
                queryParams: pseid ? { pid, pseid } : { pid },
            }),
        );
    });

    openPdfProspect(publication: INewDwellingDocumentInfo) {
        this.newDwellingService.getAndOpenDocument(publication);
    }

    finnUrl = computed(() => {
        return `https://www.finn.no/${this.dwellingStageInfo()?.finnId}`;
    });

    openProspect(url: string) {
        if (!url.includes("https")) {
            url = "https://" + url;
        }
        window.open(url, "_blank");
    }

    stages = computed(() => this.dwellingProjectInfo()?.stageEstateTypesModel?.projectStageEstateTypes ?? []);

    // Determine lat/lon for map navigation based on dwelling or project data
    // Prioritize dwelling coordinates, then project coordinates, then first stage estate type with coordinates
    projectLatLon = computed((): { lat: number; lon: number } | null => {
        if (!this.dwellingProjectInfo()) {
            return null;
        }

        if (this.dwellingProjectInfo().latitude != null && this.dwellingProjectInfo().longitude != null) {
            return { lat: this.dwellingProjectInfo().latitude, lon: this.dwellingProjectInfo().longitude };
        }

        if (this.stages() && this.stages().length > 0) {
            const firstWithLatLon = this.stages().find((se) => se.latitude != null && se.longitude != null);
            if (firstWithLatLon) {
                return { lat: firstWithLatLon.latitude, lon: firstWithLatLon.longitude };
            }
        }

        return null;
    });

    mapLatLon = computed((): { lat: number; lon: number } | null => {
        if (this.dwellingStageInfo()) {
            return { lat: this.dwellingStageInfo().latitude, lon: this.dwellingStageInfo().longitude };
        }

        return this.projectLatLon();
    });

    evKartLink = computed(() => {
        const lat = this.mapLatLon()?.lat;
        const lon = this.mapLatLon()?.lon;
        const pid = this.dwellingStageInfo()?.projectId ?? this.dwellingProjectInfo()?.unitId;
        const pseid = this.stageMode() ? this.dwellingStageInfo()?.projectStageEstateTypeId : null;
        return `/map/new-dwellings/market?lat=${lat}&lon=${lon}&pid=${pid}${pseid ? `&pseid=${pseid}` : ""}&zoom=16`;
    });

    priceList = computed(() => {
        return this.stageMode() ? this.stagePriceLists() : this.projectPriceLists();
    });

    prospects = computed(() => {
        return this.stageMode() ? this.stageProspects() : [];
    });

    projectPublicationsList = computed(() => {
        return this.publications() ?? [];
    });

    private projectPriceLists = computed(() => {
        return this.dwellingProjectInfo()?.priceList ?? [];
    });

    private stagePriceLists = computed(() => {
        return this.dwellingStageInfo()?.priceList ?? [];
    });

    private stageProspects = computed(() => {
        return this.dwellingStageInfo()?.prospects ?? [];
    });
}
